import "./PollyIcon.scss";

import React from "react";

import { useTheme } from "../state/ThemeProvider";

type PollyIconProps = {
  className?: string;
  icon: PollyIconName;
  style?: React.CSSProperties;
  ignoreTheme?: boolean;
  primary?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  labelPlacement?: LabelPlacement;
  inheritColor?: boolean;
  size?: Sizes;
};

type Sizes = "small" | "large" | "xl";

type LabelPlacement = "before" | "after";

export const PollyIcon: React.FC<PollyIconProps> = ({
  className = "",
  icon,
  style,
  label,
  ignoreTheme = false,
  primary = false,
  disabled = false,
  inheritColor,
  size,
  labelPlacement = "after",
}) => {
  const { themeName } = useTheme();

  const pollyIcon = (
    <i
      className={calculateClassNames(
        icon,
        ignoreTheme,
        themeName,
        primary,
        className,
        disabled,
        inheritColor,
        size
      )}
      style={style}
    />
  );

  if (label) {
    if (labelPlacement === "after") {
      return (
        <div className="polly-icon-component polly-icon-component--label-after">
          {pollyIcon}
          {label}
        </div>
      );
    } else {
      return (
        <div className="polly-icon-component polly-icon-component--label-before">
          {label}
          {pollyIcon}
        </div>
      );
    }
  }

  return pollyIcon;
};

const calculateClassNames = (
  icon: PollyIconName,
  ignoreTheme: boolean,
  themeName: string,
  primary: boolean,
  className?: string,
  disabled?: boolean,
  inheritColor?: boolean,
  size?: Sizes
) => {
  let calculatedClassName = `${className} icon-${icon} `;

  if (size === "small") {
    calculatedClassName += " polly-icon-component__small";
  }

  if (size === "large") {
    calculatedClassName += " polly-icon-component__large";
  }

  if (size === "xl") {
    calculatedClassName += " polly-icon-component__xl";
  }

  if (inheritColor) {
    return `${calculatedClassName} polly-icon--inherit`;
  } else {
    calculatedClassName += `${calculatedClassName} polly-accent-color`;
  }

  if (ignoreTheme) {
    return calculatedClassName;
  }
  calculatedClassName = `${calculatedClassName} polly-icon--${themeName}${
    !primary ? "--secondary" : ""
  }${disabled ? "--disabled" : ""}`;

  return calculatedClassName;
};

// Follow this guide to add new icons:
// https://www.notion.so/pollyai/IcoMoon-updating-project-fonts-4f3af197efbe4d2ebca7069e3e7a5300#a98a7e1c3de94683b8114b540bae59a9
// We use IcoMoon, which converts icons into unicode characters. We update our font file with new icon characters.

export type PollyIconName =
  | "close-poll-icon"
  | "trash-icon"
  | "survey-question-icon"
  | "add-question-icon"
  | "results-settings-icon"
  | "survey-settings-icon"
  | "audience-settings-icon"
  | "add-choice-icon"
  | "search-icon"
  | "create-icon"
  | "view-icon1"
  | "edit-icon"
  | "disable-icon"
  | "caution-icon"
  | "clock-icon"
  | "pay-icon"
  | "office-icon"
  | "announcement-icon"
  | "dropdown-caret-icon"
  | "trial-icon"
  | "download-icon"
  | "billing-icon"
  | "close-modal-icon"
  | "not-included-icon"
  | "included-icon"
  | "pay-outline-icon"
  | "link-icon"
  | "share-icon"
  | "q-info-icon"
  | "download-icon"
  | "agile-load-table-icon"
  | "plus-icon"
  | "minus-icon"
  | "arrow-icon"
  | "opsgenie-icon"
  | "github-icon"
  | "jira-icon"
  | "your-content-icon"
  | "all-channel-content-icon"
  | "all-your-content-icon"
  | "my-templates-icon"
  | "all-channel-results-icon"
  | "global-templates-icon"
  | "enable-icon"
  | "azure-devops-icon"
  | "info-icon"
  | "integration-icon"
  | "comments-count-icon"
  | "vote-count-icon"
  | "completions-icon"
  | "leaderboard-icon"
  | "quiz-start-icon"
  | "correct-icon"
  | "expand-icon"
  | "collapse-icon"
  | "subscription-icon"
  | "notification-icon"
  | "incorrect-icon"
  | "lightbulb-icon"
  | "Meetings-content-icon"
  | "qa-icon"
  | "thumbs-up-icon"
  | "thumbs-down-icon"
  | "profile-icon"
  | "save-icon"
  | "send-icon"
  | "anonomous-icon"
  | "view-icon"
  | "duplicate-icon"
  | "rating-icon"
  | "open-text-icon"
  | "multiple-choice-icon"
  | "youtube-play-icon"
  | "education"
  | "templates_2"
  | "team"
  | "meetings"
  | "guide"
  | "notify"
  | "reopen-icon"
  | "export-icon"
  | "duplicate-polly-icon"
  | "question-expand-icon"
  | "quiz"
  | "rating-icon-borderless"
  | "billing-icon"
  | "briefcase"
  | "arrow-right"
  | "message-icon"
  | "blue-check-icon"
  | "up-caret-icon"
  | "down-caret-icon"
  | "templates-icon"
  | "bolt"
  | "plus-outlined"
  | "yellow-right-arrow"
  | "consent-graph"
  | "consent-info"
  | "consent-magnifier"
  | "shuffle-icon"
  | "hot-takes"
  | "icebreaker"
  | "emoji-reaction"
  | "ai-magic-icon";
